/**
 * The shape a settings section takes when its body is a LIST rather than a form.
 *
 * Most sections just flow and let the content pane scroll them
 * (`SETTINGS_PANE_SCROLL`). The list sections do not: the audit log (both the
 * organization one and My activity) and Recycle bin are a toolbar over a table,
 * and a table wants to keep its head and its pager pinned while its rows move.
 * That only works if the table is inside a box with a known height, and it only
 * stays inside that box if the box clips.
 *
 * WHY `h-full` AND NOT A VIEWPORT FRACTION. The list should be as tall as the
 * space it is given — a 27" screen showing the same twelve rows as a laptop,
 * with the pager stranded mid-canvas and a third of the pane empty under it,
 * was the bug. So the shell fills its parent.
 *
 * That only works because every mount site now hands it a height that
 * RESOLVES. `h-full` is a percentage, and a percentage against a content-sized
 * parent does not resolve — CSS silently falls back to `auto`, the box grows to
 * its rows, and the list stops scrolling because there is nothing to scroll
 * within. That was the original failure, and the reason this used to be stated
 * in viewport units. The chain is now:
 *
 *   settings   `SettingsLayout` marks `layout: 'list'` sections and gives the
 *              pane's centred reading column `SETTINGS_PANE_FILL_COLUMN`
 *              (`flex flex-1 flex-col` with the same floor) instead of leaving
 *              it a content-sized block. Sections built on `SettingsSection`
 *              get the same thing through its `fill` prop, which carries down
 *              to `FormSection` and `GlassCard`.
 *   elsewhere  every other mount site (`AdminPage`, `AccountSettings`, the
 *              legacy `SettingsModal`, `RecycleBinViewPage`) already nests this
 *              under a definite-height flex column, so `h-full` resolves there
 *              unchanged.
 *
 * WHY A `min-h` FLOOR AND NOT `min-h-0`. Filling is only right while there is
 * something to fill. Under roughly 20rem the toolbar plus a sticky head plus a
 * pager leaves no room for a row, so the shell stops shrinking and the pane
 * (which is the scroller) takes over. `min-h-0` is unnecessary alongside
 * `h-full` — the height is already definite, so the box never needs permission
 * to shrink below its content.
 *
 * WHY `overflow-hidden` IS NOT OPTIONAL. A `max-height` alone does not contain
 * anything: overflow defaults to `visible`, so when the toolbar wraps on a short
 * viewport and the children's minimum heights add up past the cap, the rows
 * simply paint outside the card — past its rounded corner and over whatever is
 * below. The cap stops the box growing; only `overflow-hidden` stops the
 * content leaving it.
 *
 * THE INVARIANT, in one line: exactly one scroller, and it is inside the
 * surface — the shell clips, the chrome does not shrink, the surface clips, and
 * the table's own container is the only thing that scrolls.
 */

/** The shortest a list is still worth drawing as a list. See the note above. */
export const LIST_SECTION_MIN_HEIGHT = 'min-h-[20rem]';

/**
 * The section root: a clipped flex column that fills the height it is given.
 * Everything inside it is measured against this box.
 */
export const LIST_SECTION_SHELL =
  `flex h-full ${LIST_SECTION_MIN_HEIGHT} min-w-0 flex-col gap-3 overflow-hidden`;

/** The fixed toolbar band — filters, search, banners. Never shrinks, never scrolls. */
export const LIST_SECTION_CHROME = 'shrink-0 space-y-3';

/**
 * The glass surface holding the table. `flex-1 min-h-0` is what lets it take
 * the space the chrome left and then shrink below its content; `overflow-hidden`
 * is what keeps the rows inside its rounded edge.
 *
 * Pair it with `<GlassCard padding="none" bodyClassName="flex min-h-0 min-w-0
 * flex-1 flex-col overflow-hidden">` and a `<Table min="min-h-0"
 * max="max-h-full">`, whose own container is then the single scroll region.
 *
 * The `bodyClassName` is load-bearing and easy to miss: GlassCard always wraps
 * its children in a body div, and an unstyled one is a plain block sized by its
 * content. The table's `h-full` then resolves against `auto`, the table grows to
 * all of its rows, and the card clips them with no scroller anywhere — the same
 * failure as an unresolvable height, one level further in.
 */
export const LIST_SECTION_SURFACE =
  'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden';
